/**
 * Idées d'accords, moments et cocktails — Vignerons de la Cave de Pierrefeu
 *
 * Lit une feuille Google Sheets publiée en CSV (colonnes attendues :
 * Cuvée, Couleur, Type, Titre, Description, Photo) et affiche des cartes.
 *
 * Deux modes, définis par data-mode sur le conteneur :
 * - "gallery" : affiche tout, avec filtres Couleur / Type (page dédiée)
 * - "product" : affiche uniquement les lignes dont la Cuvée correspond
 *   exactement à data-product-title (section fiche produit, sans filtres)
 */

(function () {
  class AccordsVigneron {
    constructor(root) {
      this.root = root;
      this.sheetUrl = root.dataset.sheetUrl;
      this.mode = root.dataset.mode || "gallery";
      this.productTitle = root.dataset.productTitle || "";
      this.rows = [];
      this.activeColor = "Tous";
      this.activeCuisine = "Tous";
      this.activeMoment = "Tous";
      this.init();
    }

    async init() {
      if (!this.sheetUrl) return;

      if (typeof Papa === "undefined") {
        console.error("PapaParse n'est pas chargé.");
        return;
      }

      Papa.parse(this.sheetUrl, {
        download: true,
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),
        transform: (v) => (typeof v === "string" ? v.trim() : v),
        complete: (results) => {
          this.rows = results.data.filter((r) => r.Cuvée && r.Titre);
          if (this.mode === "product") {
            this.rows = this.rows.filter(
              (r) => r["Cuvée"].trim() === this.productTitle.trim()
            );
            if (this.rows.length === 0) {
              this.root.classList.add("av-hidden");
              return;
            }
          }
          this.render();
        },
        error: () => {
          this.root.innerHTML = "";
        }
      });
    }

    getFiltered() {
      return this.rows.filter((r) => {
        const colorOk = this.activeColor === "Tous" || r["Couleur"] === this.activeColor;
        const cuisineOk = this.activeCuisine === "Tous" || r["Cuisine"] === this.activeCuisine;
        const momentOk = this.activeMoment === "Tous" || r["Ambiance"] === this.activeMoment;
        return colorOk && cuisineOk && momentOk;
      });
    }

    uniqueValues(column) {
      const seen = new Set();
      this.rows.forEach((r) => {
        const v = (r[column] || "").trim();
        if (v) seen.add(v);
      });
      return ["Tous", ...Array.from(seen).sort((a, b) => a.localeCompare(b, "fr"))];
    }

    render() {
      const isGallery = this.mode === "gallery";
      this.root.innerHTML = `
        ${this.root.dataset.title ? `<h2 class="av-title">${this.root.dataset.title}</h2>` : ""}
        ${isGallery ? this.filtersHtml() : ""}
        <div class="av-grid" data-av-grid></div>
      `;

      if (isGallery) {
        this.root.querySelectorAll("[data-color]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.activeColor = btn.dataset.color;
            this.updateActiveChips();
            this.renderGrid();
          });
        });
        this.root.querySelectorAll("[data-cuisine]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.activeCuisine = btn.dataset.cuisine;
            this.updateActiveChips();
            this.renderGrid();
          });
        });
        this.root.querySelectorAll("[data-moment]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.activeMoment = btn.dataset.moment;
            this.updateActiveChips();
            this.renderGrid();
          });
        });
      }

      this.renderGrid();
    }

    filtersHtml() {
      const colors = ["Tous", "Blanc", "Rosé", "Rouge"];
      const cuisines = this.uniqueValues("Cuisine");
      const moments = this.uniqueValues("Ambiance");
      const row = (label, items, attr, active) => `
        <div class="av-filter-row">
          <span class="av-filter-label">${label}</span>
          <div class="av-filter-scroll">
            ${items.map((v) => `<button type="button" class="av-chip${v === active ? " is-active" : ""}" data-${attr}="${v}">${v}</button>`).join("")}
          </div>
        </div>
      `;
      return `
        <div class="av-filters">
          ${row("Couleur", colors, "color", this.activeColor)}
          ${row("Cuisine", cuisines, "cuisine", this.activeCuisine)}
          ${row("Ambiance", moments, "moment", this.activeMoment)}
        </div>
      `;
    }

    updateActiveChips() {
      this.root.querySelectorAll("[data-color]").forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.color === this.activeColor);
      });
      this.root.querySelectorAll("[data-cuisine]").forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.cuisine === this.activeCuisine);
      });
      this.root.querySelectorAll("[data-moment]").forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.moment === this.activeMoment);
      });
    }

    renderGrid() {
      const grid = this.root.querySelector("[data-av-grid]");
      const items = this.getFiltered();

      if (items.length === 0) {
        grid.innerHTML = `<p class="av-empty">Aucune idée pour cette combinaison pour le moment.</p>`;
        return;
      }

      grid.innerHTML = items
        .map((r) => {
          const image = r["Photo"]
            ? `<div class="av-card-image"><img src="${r["Photo"]}" alt="${r["Titre"]}" loading="lazy"></div>`
            : "";
          const cuveeLink = this.mode === "gallery"
            ? `<p class="av-card-cuvee">${r["Cuvée"]}</p>`
            : "";
          return `
            <article class="av-card">
              ${image}
              <div class="av-card-body">
                <p class="av-card-type">${r["Type"] || ""}</p>
                <h3 class="av-card-title">${r["Titre"]}</h3>
                ${r["Description"] ? `<p class="av-card-desc">${r["Description"]}</p>` : ""}
                ${cuveeLink}
              </div>
            </article>
          `;
        })
        .join("");
    }
  }

  document.querySelectorAll("[data-accords-vigneron]").forEach((el) => new AccordsVigneron(el));
})();